const SITE_URL = "https://persianas-toldos-malaga.es";

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "HomeAndConstructionBusiness",
  "@id": `${SITE_URL}/#business`,
  name: "Persianas Toldos Málaga",
  image: `${SITE_URL}/og/og-default.jpg`,
  url: SITE_URL,
  telephone: "+34951120770",
  email: "info@persianas-toldos-malaga.es",
  priceRange: "€€",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Calle Pacífico 54",
    addressLocality: "Málaga",
    addressRegion: "Andalucía",
    postalCode: "29003",
    addressCountry: "ES",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 36.7213,
    longitude: -4.4214,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "20:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Saturday"],
      opens: "09:00",
      closes: "14:00",
    },
  ],
  areaServed: [
    { "@type": "City", name: "Málaga" },
    { "@type": "City", name: "Marbella" },
    { "@type": "City", name: "Fuengirola" },
    { "@type": "City", name: "Torremolinos" },
    { "@type": "City", name: "Benalmádena" },
    { "@type": "City", name: "Mijas" },
    { "@type": "City", name: "Estepona" },
  ],
  sameAs: [],
};

export interface BreadcrumbItem {
  name: string;
  path?: string;
}

export function generateBreadcrumbSchema(
  breadcrumbs: BreadcrumbItem[]
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: crumb.name,
      ...(crumb.path ? { item: `${SITE_URL}${crumb.path}` } : {}),
    })),
  };
}

export function generateServiceSchema(
  serviceType: string,
  url: string,
  description: string
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    provider: { "@id": `${SITE_URL}/#business` },
    serviceType,
    url: `${SITE_URL}${url}`,
    description,
    areaServed: {
      "@type": "City",
      name: "Málaga",
      containedInPlace: { "@type": "Country", name: "España" },
    },
  };
}

export function generateServiceOfferCatalogSchema(
  serviceType: string,
  url: string,
  description: string,
  offers: Array<{ name: string; price: string; priceCurrency?: string }>
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType,
    provider: {
      "@type": "HomeAndConstructionBusiness",
      "@id": `${SITE_URL}/#business`,
      name: "Persianas Toldos Málaga",
    },
    areaServed: {
      "@type": "State",
      name: "Málaga",
    },
    url: `${SITE_URL}${url}`,
    description,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: `Servicios de ${serviceType.toLowerCase()}`,
      itemListElement: offers.map((offer) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: offer.name,
        },
        price: offer.price,
        priceCurrency: offer.priceCurrency || "EUR",
      })),
    },
  };
}

export function generateFAQPageSchema(
  questions: Array<{ q: string; a: string }>
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}

export function generateCollectionPageSchema(
  name: string,
  url: string,
  description: string,
  itemUrls: string[]
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url: `${SITE_URL}${url}`,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: itemUrls.map((itemUrl, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE_URL}${itemUrl}`,
      })),
    },
  };
}

export function generateWebPageSchema(
  url: string,
  name: string,
  description?: string
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${SITE_URL}${url}`,
    url: `${SITE_URL}${url}`,
    name,
    ...(description ? { description } : {}),
  };
}

export interface PageData {
  breadcrumbs?: BreadcrumbItem[];
  title?: string;
  description?: string;
  faqs?: Array<{ q: string; a: string }>;
  offers?: Array<{ name: string; price: string; priceCurrency?: string }>;
}

function capitalizeCity(slug: string): string {
  if (slug === "malaga") return "Málaga";
  if (slug === "benalmadena") return "Benalmádena";
  if (slug === "velez-malaga") return "Vélez-Málaga";
  if (slug === "rincon-de-la-victoria") return "Rincón de la Victoria";
  if (slug === "san-pedro-de-alcantara") return "San Pedro de Alcántara";
  if (slug === "alhaurin-de-la-torre") return "Alhaurín de la Torre";
  if (slug === "alhaurin-el-grande") return "Alhaurín el Grande";
  if (slug === "torremolinos") return "Torremolinos";
  if (slug === "fuengirola") return "Fuengirola";
  if (slug === "marbella") return "Marbella";
  if (slug === "estepona") return "Estepona";
  if (slug === "mijas") return "Mijas";
  if (slug === "antequera") return "Antequera";
  if (slug === "nerja") return "Nerja";
  if (slug === "ronda") return "Ronda";
  if (slug === "frigiliana") return "Frigiliana";
  if (slug === "alora") return "Álora";
  if (slug === "cartama") return "Cártama";
  if (slug === "coin") return "Coín";
  if (slug === "benahavis") return "Benahavís";
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function getSchemas(
  pathname: string,
  pageData?: PageData
): Record<string, unknown>[] {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const schemas: Record<string, unknown>[] = [];

  // 1. LocalBusiness en TODAS las páginas
  schemas.push(localBusinessSchema);

  // 2. Breadcrumbs si existen
  if (pageData?.breadcrumbs && pageData.breadcrumbs.length > 0) {
    schemas.push(generateBreadcrumbSchema(pageData.breadcrumbs));
  }

  // 3. Schemas específicos por ruta
  if (normalized === "/") {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Persianas Toldos Málaga",
      url: SITE_URL,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/?s={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    });
    schemas.push(
      generateWebPageSchema(
        "/",
        pageData?.title || "Persianas Toldos Málaga",
        pageData?.description
      )
    );
  } else if (normalized === "/persianas") {
    schemas.push(
      generateServiceOfferCatalogSchema(
        "Reparación de persianas",
        "/persianas/",
        "Servicio profesional de reparación de persianas en Málaga. Cambio de cinta, motor, lamas y persianas eléctricas.",
        [
          { name: "Cambio de cinta", price: "60" },
          { name: "Reparación motor", price: "80" },
          { name: "Sustitución motor", price: "150" },
        ]
      )
    );
    schemas.push(
      generateCollectionPageSchema(
        "Servicios de persianas en Málaga y Costa del Sol",
        "/persianas/",
        "Catálogo completo de servicios de persianas: reparación, motor, cinta, instalación. Cobertura en toda la provincia de Málaga.",
        [
          "/persianas/malaga/",
          "/persianas/marbella/",
          "/persianas/fuengirola/",
          "/persianas/torremolinos/",
          "/persianas/benalmadena/",
          "/persianas/mijas/",
          "/persianas/estepona/",
          "/persianas/velez-malaga/",
          "/persianas/rincon-de-la-victoria/",
          "/persianas/benahavis/",
          "/persianas/san-pedro-de-alcantara/",
          "/persianas/alhaurin-de-la-torre/",
          "/persianas/alhaurin-el-grande/",
          "/persianas/antequera/",
          "/persianas/ronda/",
          "/persianas/nerja/",
          "/persianas/cartama/",
          "/persianas/coin/",
          "/persianas/alora/",
          "/persianas/reparacion-persianas-malaga/",
          "/persianas/motor-persianas/",
          "/persianas/persianas-electricas/",
          "/persianas/cambiar-cinta-persiana/",
          "/persianas/persianas-aluminio/",
          "/persianas/persianas-pvc/",
          "/persianas/precios-persianas/",
        ]
      )
    );
  } else if (normalized === "/toldos") {
    schemas.push(
      generateServiceOfferCatalogSchema(
        "Instalación de toldos",
        "/toldos/",
        "Instalación y motorización de toldos en Málaga. Toldos motorizados, cofre, extensibles y de balcón.",
        [
          { name: "Toldo balcón básico", price: "350" },
          { name: "Toldo cofre 3x2m", price: "850" },
          { name: "Toldo motorizado 4x3m", price: "1200" },
        ]
      )
    );
    schemas.push(
      generateCollectionPageSchema(
        "Servicios de toldos en Málaga y Costa del Sol",
        "/toldos/",
        "Catálogo completo de toldos: motorizados, cofre, extensibles, pérgolas y reparación. Instalación en toda la provincia de Málaga.",
        [
          "/toldos/malaga/",
          "/toldos/marbella/",
          "/toldos/fuengirola/",
          "/toldos/torremolinos/",
          "/toldos/benalmadena/",
          "/toldos/mijas/",
          "/toldos/estepona/",
          "/toldos/nerja/",
          "/toldos/velez-malaga/",
          "/toldos/antequera/",
          "/toldos/toldos-motorizados/",
          "/toldos/toldos-cofre/",
          "/toldos/toldos-extensibles/",
          "/toldos/toldos-veranda/",
          "/toldos/toldos-balcon/",
          "/toldos/reparacion-toldos/",
          "/toldos/instalacion-toldos/",
          "/toldos/precios-toldos/",
        ]
      )
    );
  } else if (normalized === "/mosquiteras") {
    schemas.push(
      generateServiceOfferCatalogSchema(
        "Instalación de mosquiteras",
        "/mosquiteras/",
        "Instalación de mosquiteras enrollables, correderas y fijas en Málaga. Protección contra mosquitos desde 60€.",
        [
          { name: "Mosquitera fija", price: "60" },
          { name: "Mosquitera corredera", price: "90" },
          { name: "Mosquitera enrollable", price: "120" },
        ]
      )
    );
    schemas.push(
      generateCollectionPageSchema(
        "Servicios de mosquiteras en Málaga y Costa del Sol",
        "/mosquiteras/",
        "Catálogo completo de mosquiteras: enrollables, correderas, fijas y reparación. Instalación en toda la provincia de Málaga.",
        [
          "/mosquiteras/malaga/",
          "/mosquiteras/marbella/",
          "/mosquiteras/fuengirola/",
          "/mosquiteras/torremolinos/",
          "/mosquiteras/benalmadena/",
          "/mosquiteras/mijas/",
          "/mosquiteras/estepona/",
          "/mosquiteras/nerja/",
          "/mosquiteras/velez-malaga/",
          "/mosquiteras/antequera/",
          "/mosquiteras/mosquiteras-enrollables/",
          "/mosquiteras/mosquiteras-correderas/",
          "/mosquiteras/mosquiteras-fijas/",
          "/mosquiteras/reparacion-mosquiteras/",
        ]
      )
    );
  } else if (normalized === "/contacto") {
    schemas.push(
      generateWebPageSchema(
        "/contacto/",
        pageData?.title || "Contacto",
        pageData?.description
      )
    );
    schemas.push({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "@id": `${SITE_URL}/contacto/`,
      url: `${SITE_URL}/contacto/`,
      name: pageData?.title || "Contacto",
      ...(pageData?.description ? { description: pageData.description } : {}),
      mainEntity: { "@id": `${SITE_URL}/#business` },
    });
  } else if (normalized === "/zonas") {
    schemas.push(
      generateWebPageSchema(
        "/zonas/",
        pageData?.title || "Zonas",
        pageData?.description
      )
    );
    schemas.push(
      generateCollectionPageSchema(
        "Zonas de servicio en Málaga y Costa del Sol",
        "/zonas/",
        "Cobertura completa de Persianas Toldos Málaga: ciudades principales y comarcas del interior de la provincia.",
        [
          "/zonas/malaga/",
          "/zonas/marbella/",
          "/zonas/fuengirola/",
          "/zonas/torremolinos/",
          "/zonas/benalmadena/",
          "/zonas/mijas/",
          "/zonas/estepona/",
          "/zonas/velez-malaga/",
          "/zonas/rincon-de-la-victoria/",
          "/zonas/benahavis/",
          "/zonas/san-pedro-de-alcantara/",
          "/zonas/axarquia/",
          "/zonas/valle-del-guadalhorce/",
          "/zonas/serrania-de-ronda/",
        ]
      )
    );
  } else if (normalized === "/presupuesto") {
    schemas.push(
      generateWebPageSchema(
        "/presupuesto/",
        pageData?.title || "Presupuesto",
        pageData?.description
      )
    );
  } else if (normalized === "/sobre-nosotros") {
    schemas.push(
      generateWebPageSchema(
        "/sobre-nosotros/",
        pageData?.title || "Quiénes Somos",
        pageData?.description
      )
    );
    schemas.push({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "@id": `${SITE_URL}/sobre-nosotros/`,
      url: `${SITE_URL}/sobre-nosotros/`,
      name: pageData?.title || "Quiénes Somos",
      ...(pageData?.description ? { description: pageData.description } : {}),
    });
  } else if (normalized.startsWith("/persianas/")) {
    const sub = normalized.replace("/persianas/", "");
    const cityMatch = sub.match(/^([^/]+)$/);
    if (cityMatch) {
      const city = capitalizeCity(cityMatch[1]);
      if (pageData?.offers && pageData.offers.length > 0) {
        schemas.push(
          generateServiceOfferCatalogSchema(
            `Reparación de persianas en ${city}`,
            `${normalized}/`,
            `Servicio de reparación e instalación de persianas en ${city} y alrededores. Visita 24h.`,
            pageData.offers
          )
        );
      } else {
        schemas.push(
          generateServiceSchema(
            `Reparación de persianas en ${city}`,
            `${normalized}/`,
            `Servicio de reparación e instalación de persianas en ${city} y alrededores. Visita 24h.`
          )
        );
      }
    } else {
      // Sub-página de servicio específico (ej: /persianas/motor-persianas)
      schemas.push(
        generateServiceSchema(
          "Reparación de persianas",
          `${normalized}/`,
          pageData?.description || "Servicio profesional de persianas en Málaga."
        )
      );
    }
    schemas.push(
      generateWebPageSchema(
        `${normalized}/`,
        pageData?.title || "Persianas",
        pageData?.description
      )
    );
  } else if (normalized.startsWith("/toldos/")) {
    const sub = normalized.replace("/toldos/", "");
    const cityMatch = sub.match(/^([^/]+)$/);
    if (cityMatch) {
      const city = capitalizeCity(cityMatch[1]);
      schemas.push(
        generateServiceSchema(
          `Instalación de toldos en ${city}`,
          `${normalized}/`,
          `Servicio de instalación y reparación de toldos en ${city} y alrededores. Visita 24h.`
        )
      );
    } else {
      schemas.push(
        generateServiceSchema(
          "Instalación de toldos",
          `${normalized}/`,
          pageData?.description || "Servicio profesional de toldos en Málaga."
        )
      );
    }
    schemas.push(
      generateWebPageSchema(
        `${normalized}/`,
        pageData?.title || "Toldos",
        pageData?.description
      )
    );
  } else if (normalized.startsWith("/mosquiteras/")) {
    const sub = normalized.replace("/mosquiteras/", "");
    const cityMatch = sub.match(/^([^/]+)$/);
    if (cityMatch) {
      const city = capitalizeCity(cityMatch[1]);
      schemas.push(
        generateServiceSchema(
          `Instalación de mosquiteras en ${city}`,
          `${normalized}/`,
          `Servicio de instalación y reparación de mosquiteras en ${city} y alrededores. Visita 24h.`
        )
      );
    } else {
      schemas.push(
        generateServiceSchema(
          "Instalación de mosquiteras",
          `${normalized}/`,
          pageData?.description || "Servicio profesional de mosquiteras en Málaga."
        )
      );
    }
    schemas.push(
      generateWebPageSchema(
        `${normalized}/`,
        pageData?.title || "Mosquiteras",
        pageData?.description
      )
    );
  } else if (normalized.startsWith("/zonas/")) {
    const sub = normalized.replace("/zonas/", "");
    const cityMatch = sub.match(/^([^/]+)$/);
    if (cityMatch) {
      const city = capitalizeCity(cityMatch[1]);
      schemas.push(
        generateServiceSchema(
          `Servicio de persianas, toldos y mosquiteras en ${city}`,
          `${normalized}/`,
          `Reparación e instalación de persianas, toldos y mosquiteras en ${city} y alrededores. Visita 24h.`
        )
      );
    }
    schemas.push(
      generateWebPageSchema(
        `${normalized}/`,
        pageData?.title || "Zonas",
        pageData?.description
      )
    );
  } else {
    // Fallback: WebPage genérico para cualquier otra ruta
    schemas.push(
      generateWebPageSchema(
        `${normalized}/`,
        pageData?.title || "Persianas Toldos Málaga",
        pageData?.description
      )
    );
  }

  // 4. FAQ si existen en pageData
  if (pageData?.faqs && pageData.faqs.length > 0) {
    schemas.push(generateFAQPageSchema(pageData.faqs));
  }

  return schemas;
}